import api from '@components/api';
import styles from '@components/components/AddressPicker/AddressPicker.module.scss';
import { useLocalStorage, useAjax, useMedia } from '@components/hooks';
import AutocompleteInputField from '@components/components/AddressPicker/components/AutocompleteInputField/AutocompleteInputField';
import debounce from 'lodash.debounce';
import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { Button, Content, Skeleton } from '@wienenergiegithub/ui-next';
import classNames from 'classnames';
import { Form, FormSubmit, useFormStore, useMenuStore } from '@ariakit/react';
import { CancelToken } from 'axios';
import PropTypes from 'prop-types';
import Error from '../HeatRecommender/Responses/Error/Error';
import {
  checkIfHeatDataUnavailable,
  getFernwaermeCase,
  defaultValuesAddressPicker,
  fieldDependencies,
} from './AddressPicker.config';

let cancel;

const AddressPicker = ({
  buttonId,
  buttonText = 'Überprüfen',
  buttonTheme = 'primary',
  onError,
  onSuccess,
  subtitle = null,
  type,
}) => {
  const defaultSuggestions = {
    plzOrt: [],
    gemeinde: [],
    strasse: [],
    gebadr1: [],
    stiege: [],
    tuerNr: [],
  };
  const [storage, setStorage] = useLocalStorage(
    'address-check',
    defaultValuesAddressPicker,
  );
  const [heatStorage, setHeatStorage] = useLocalStorage('heat-info');
  const [storageTkconnid, setStorageTkconnid] = useLocalStorage('tkconnid', {});
  const [suggestions, setSuggestions] = useState(defaultSuggestions);
  const [isLoading, setIsLoading] = useState(false);
  const [isButtonLoading, setIsButtonLoading] = useState(false);
  const [olavKeys, setOlavKeys] = useState(storage?.olavKeys || {});
  const [serverError, setServerError] = useState(false);
  const errorRef = useRef();
  const pickerRef = useRef();
  const isMobile = useMedia('(max-width: 768px)');

  const menus = {
    plzOrt: useMenuStore({
      animated: 250,
      orientation: 'vertical',
      placement: 'bottom-start',
    }),
    gemeinde: useMenuStore({
      animated: 250,
      orientation: 'vertical',
      placement: 'bottom-start',
    }),
    strasse: useMenuStore({
      animated: 250,
      orientation: 'vertical',
      placement: 'bottom-start',
    }),
    gebadr1: useMenuStore({
      animated: 250,
      orientation: 'vertical',
      placement: 'bottom-start',
    }),
    stiege: useMenuStore({
      animated: 250,
      orientation: 'vertical',
      placement: 'bottom-start',
    }),
    tuerNr: useMenuStore({
      animated: 250,
      orientation: 'vertical',
      placement: 'bottom-start',
    }),
  };
  const [selectedValues, setSelectedValues] = useState([]);
  const [tkconnids, setTkconnids] = useState([]);
  const gemeindeRef = useRef(null);
  const strasseRef = useRef(null);
  const gebadr1Ref = useRef(null);
  const stiegeRef = useRef(null);
  const tuerNrRef = useRef(null);
  const submitRef = useRef(null);

  const focusDependencies = {
    plzOrt: strasseRef,
    gemeinde: strasseRef,
    strasse: gebadr1Ref,
    gebadr1: stiegeRef,
    stiege: tuerNrRef,
  };

  const fieldConditions = {
    plzOrt: value => value.length > 2,
    strasse: value => value.length > 2,
  };

  const optionalFields = ['tuerNr', 'stiege', 'tkconnid'];
  const cancelOngoingRequest = () => {
    if (cancel !== undefined) {
      setIsLoading(false);
      cancel();
    }
  };

  const getAvailableInfo = data => {
    const availableInfo = data.reduce((object, item) => {
      object[item.sparte] = item.verfuegbar;
      return object;
    }, {});
    return availableInfo;
  };

  const [values, setValues] = useState(defaultValuesAddressPicker);
  const form = useFormStore({ values, setValues });

  const getTkconidSuggestions = async inpValues => {
    const postValues = {
      anaAnlageKey: inpValues?.anlageKey,
      gaaGebaeudeKey: inpValues?.gebaeudeKey,
      aaaAnlageAdressKey: inpValues?.anlageAdressKey,
      gbaGebaeudeKeyIdent: inpValues?.gebaeudeKeyIdent,
      strasse: inpValues?.strasse,
      ort: inpValues?.gemeinde,
      hausnummer: inpValues?.gebadr1,
      plz: inpValues?.plz,
    };

    const { data: responseTkconid } = await api.post(
      '/internet/gettkconid',
      postValues,
    );

    return (
      responseTkconid?.find(productType => productType.sparte === 'Internet') ??
      null
    );
  };

  const [fetchRagAvailabilities] = useAjax(
    'we_theme_heat_fetch_rag_availabilities',
  );

  // Helper function to handle heat data requests
  const fetchHeatData = async requestParams => {
    const response = await fetchRagAvailabilities(requestParams);
    return response?.data?.data;
  };

  // Request Fernwaerme data based on the address and building key.
  // Triggered by handleSuggestionClick, form.submit, or suggestionlist === 1
  // depending on address and user interaction.
  const getHeatInformation = async (
    relevantAddressInfo,
    requestedGebaeudeKey,
  ) => {
    let heatResponse, hausNrAddressResponse;

    const { lastRequestParams, fernwaermeCase, heat } = heatStorage || {};

    if (!relevantAddressInfo || !requestedGebaeudeKey) {
      setIsButtonLoading(false);
      return;
    }

    // Check if the request parameters have changed; skip request if unchanged and valid
    const newRequestParams = {
      olavGaa: requestedGebaeudeKey,
    };

    if (
      JSON.stringify(newRequestParams) === JSON.stringify(lastRequestParams) &&
      fernwaermeCase &&
      heat &&
      fernwaermeCase !== 'heatError'
    ) {
      setIsButtonLoading(false);
      return fernwaermeCase;
    }

    try {
      // First API call to fetch heat data using the full address (with stiege)

      heatResponse = await fetchHeatData(newRequestParams);
      if (!heatResponse) {
        setHeatStorage({
          heat: {},
          fernwaermeCase: 'heatError',
          lastRequestParams: '',
        });
        return 'heatError';
      }

      const isHeatDataUnavailable = checkIfHeatDataUnavailable(heatResponse);

      // Re-query address without stiege if heat data is unavailable
      if (isHeatDataUnavailable) {
        const gebadr1 = relevantAddressInfo.gebadr1
          ? relevantAddressInfo.gebadr1
          : relevantAddressInfo.hausnummer;

        const [hausNr, stiege] = gebadr1.split('/') || [];

        // Store as 'dataIssue' if there's no stiege
        if (!stiege) {
          setHeatStorage({
            heat: heatResponse,
            fernwaermeCase: 'dataIssue',
            lastRequestParams: newRequestParams,
          });
          return 'dataIssue';
        }

        // Re-query the address without stiege
        const postValues = {
          field: 'gebadr1',
          search: hausNr || '',
          plzOrt: relevantAddressInfo.plz || '',
          gemeinde: relevantAddressInfo.gemeinde || '',
          strasse: relevantAddressInfo.strasse || '',
          stiege: '',
        };

        const addressResponse = await api.post('/address/query', postValues);
        if (!addressResponse?.data?.length) return;

        hausNrAddressResponse = addressResponse.data.find(
          item => item.gebadr1 === hausNr,
        );

        if (!hausNrAddressResponse) {
          setHeatStorage({
            heat: {},
            fernwaermeCase: 'heatError',
            lastRequestParams: '',
          });
          return 'heatError';
        }

        // Re-query heat data using the new building key (without stiege)
        const newHausNrRequestParams = {
          olavGaa: hausNrAddressResponse.gebaeudeKey,
        };

        heatResponse = await fetchHeatData(newHausNrRequestParams);

        // If still unavailable, store as 'dataIssue'
        const isStillUnavailable = checkIfHeatDataUnavailable(heatResponse);

        if (isStillUnavailable) {
          setHeatStorage({
            heat: heatResponse,
            fernwaermeCase: 'dataIssue',
            lastRequestParams: newHausNrRequestParams,
          });
          return 'dataIssue';
        }
      }

      // Store and return the fernwaerme case
      const fernwaermeCase = getFernwaermeCase(heatResponse);

      setHeatStorage({
        heat: heatResponse,
        fernwaermeCase,
        lastRequestParams: newRequestParams,
      });

      return fernwaermeCase; // Immediate return is only needed when getHeatInformation is called in the submit function
    } catch (error) {
      setHeatStorage({
        heat: {},
        fernwaermeCase: 'heatError',
        lastRequestParams: '',
      });

      return 'heatError';
    } finally {
      setIsButtonLoading(false);
    }
  };

  form.useSubmit(async () => {
    const {
      gemeinde,
      anlageKey: anaAnlageKey,
      gebaeudeKey: gaaGebaeudeKey,
      strasse,
      gebadr1,
      tuerNr: tuernummer,
      anlageAdressKey: aaaAnlageAdressKey,
      gebaeudeKeyIdent: gbaGebaeudeKeyIdent,
      plzOrt,
    } = olavKeys;

    const hausnummer =
      gebadr1?.split('/').length > 1 ? gebadr1?.split('/')[0] : gebadr1;
    const stiege = gebadr1?.split('/').length > 1 ? gebadr1?.split('/')[1] : '';

    const postValues = {
      gemeinde,
      anaAnlageKey,
      gaaGebaeudeKey,
      strasse,
      tuernummer,
      aaaAnlageAdressKey,
      hausnummer,
      gbaGebaeudeKeyIdent,
      stiege,
      plzOrt,
      plz: plzOrt?.length > 0 ? plzOrt[0] : '',
    };

    if (type === 'heatPump') {
      onSuccess(postValues);
    } else {
      let tkconnid;
      if (type === 'internet') {
        //tkconnid get value from door or get it from hausnummer
        const tkconnidMatch = tkconnids?.find(
          o => o.tuernummer == values?.tuerNr,
        )?.tkconnid;

        tkconnid = tkconnidMatch || storageTkconnid?.tkconnid;
        setStorageTkconnid({
          tkconnid,
        });
      }
      const requestData = {
        type,
        ...postValues,
        street: strasse,
        streetnr:
          hausnummer?.split('/').length > 1
            ? hausnummer?.split('/')[0]
            : hausnummer,
        staircase:
          hausnummer?.split('/').length > 1 ? hausnummer?.split('/')[1] : '',
        door: values?.tuerNr,
        zip: plzOrt?.length > 1 ? plzOrt[0] : '',
        location: plzOrt?.length > 1 ? plzOrt[1] : '',
      };

      try {
        let availabilities = [];
        setIsLoading('skeleton');

        availabilities = await api.post('/address/availabilities', requestData);

        if (availabilities?.data) {
          if (type === 'heat' || type === 'general') {
            // Check if heatStorage contains valid data for the requested building

            const isHeatStorageAvailable =
              heatStorage?.fernwaermeCase &&
              heatStorage?.lastRequestParams === postValues.gaaGebaeudeKey;

            let fernwaermeCase = heatStorage?.fernwaermeCase;

            // Re-request heat information if it's not available in storage
            if (!isHeatStorageAvailable) {
              const relevantAddressInfo = { ...postValues, gebadr1 };

              fernwaermeCase = await getHeatInformation(
                relevantAddressInfo,
                postValues.gaaGebaeudeKey,
              );
            }

            if (type === 'general') {
              onSuccess(await availabilities.data, fernwaermeCase);
            }

            if (type === 'heat') {
              onSuccess(
                await availabilities.data,
                setIsLoading,
                fernwaermeCase,
              );
            }
          } else if (type === 'internet') {
            onSuccess(await availabilities.data, postValues, tkconnid);
          } else {
            onSuccess(
              await availabilities.data,
              postValues,
              requestData,
              setIsLoading,
            );
          }
        }
        const availableInfo = getAvailableInfo(availabilities.data);
        setStorage({ ...storage, ...availableInfo });

        // send all address asked for superschnell to GIS
        if (type === 'internet' && availabilities?.data?.length) {
          await api.post('/address/gisaddress', postValues);
        }

        if (type === 'general') {
          setIsLoading(false);
        }
      } catch (error) {
        onError(error);

        if (type === 'general') {
          setIsLoading(false);
        }
      }
    }
  });

  const disabledSubmit = useMemo(
    () =>
      isLoading ||
      isButtonLoading ||
      Object.keys(selectedValues).reduce(
        (valid, key) =>
          !optionalFields.includes(key) && selectedValues[key] === ''
            ? true
            : valid,
        false,
      ),
    [
      isLoading,
      isButtonLoading,
      selectedValues.strasse,
      selectedValues.gebadr1,
      selectedValues.stiege,
      selectedValues.tuerNr,
      selectedValues.gemeinde,
      selectedValues.plzOrt,
    ],
  );

  // query the address
  // currentField: the field that is currently in focus
  // formValues: the current values of the form
  // selectedFormValues: the selectedValues
  const queryAddress = async (
    currentField,
    formValues,
    selectedFormValues,
    wasClicked,
  ) => {
    cancelOngoingRequest();

    if (
      fieldConditions[currentField] !== undefined
        ? !fieldConditions[currentField](formValues[currentField])
        : false
    ) {
      return;
    }

    const postValues = {
      field: currentField,
      search: formValues[currentField] || '*',
      plzOrt: selectedFormValues?.plzOrt?.split(' ')[0] || '',
      gemeinde: selectedFormValues?.plzOrt?.split(' ')[1] || '',
      strasse: selectedFormValues?.strasse || '',
      gebadr1: selectedFormValues?.gebadr1 || '',
      stiege: selectedFormValues?.stiege || '',
    };
    delete postValues[currentField];
    setIsLoading(true);

    try {
      const response = await api.post('/address/query', postValues, {
        cancelToken: new CancelToken(c => {
          cancel = c;
        }),
      });

      if (!response.data.length) {
        return;
      }
      const rawData = response.data;

      let suggestionTkconnid;
      const suggestionsList = rawData.map(item => {
        if (Array.isArray(item[currentField])) {
          return {
            label: item[currentField].join(' '),
            value: item[currentField].join(' '),
            icon: true,
            type: 'result',
          };
        }
        return {
          label: item[currentField],
          value: item[currentField],
          icon: true,
          type: 'result',
        };
      });

      // we need to set newOlavKeys at the end of each addressQuery
      // this is needed later for the submit function (availabilities, availability)
      // we compare currentField of the rawData (return from query)
      // with the value of the currentField (from the form)
      setOlavKeys(
        rawData.filter(
          item => item[currentField] === formValues[currentField],
        )[0] || false,
      );

      if (suggestionsList.length === 1) {
        setSelectedValues(prev => ({
          ...prev,
          [currentField]: suggestionsList[0].value,
        }));

        if (type === 'internet' && currentField === 'gebadr1') {
          const rawTkconnid = await getTkconidSuggestions(
            rawData?.find(adr => adr.gebadr1 === formValues[currentField]),
          );

          if (rawTkconnid?.verfuegbar !== 'NEIN') {
            suggestionTkconnid = rawTkconnid?.details.map(item => ({
              label: item?.tuernummer,
              value: item?.tuernummer,
              icon: true,
              type: 'result',
            }));
          }
          const houseTkconnid = rawTkconnid.details?.find(
            id => id.tuernummer === null,
          );

          if (houseTkconnid) {
            setStorageTkconnid({
              tkconnid: houseTkconnid?.tkconnid,
            });
          }

          setTkconnids(rawTkconnid.details);
        }
        if (type === 'heat' && currentField === 'gebadr1') {
          const relevantAddressInfo = rawData.filter(
            item => item[currentField] === formValues[currentField],
          )[0];

          setIsButtonLoading(true);
          getHeatInformation(
            relevantAddressInfo,
            relevantAddressInfo?.gebaeudeKey,
          );
        }
        if (
          currentField === 'gebadr1' &&
          suggestionsList[0].value.split('/').length > 1
        ) {
          setSelectedValues(prev => ({
            ...prev,
            stiege: suggestionsList[0].value.split('/')[1],
          }));
        }

        if (
          currentField === 'plzOrt' &&
          suggestionsList[0].value.split(' ').length > 1
        ) {
          setSelectedValues(prev => ({
            ...prev,
            gemeinde: suggestionsList[0].value.split(' ')[1],
          }));
        }
        // here we try again to set the olavKeys
        // this time we compare the currentField of the rawData
        // with the value of the result from the suggestionsList
        // this is because the form value is not always the same as the result from the suggestionsList
        // e.g. "Kirche", the suggestionsList overwrites the value of the form field (e.g. "ki")
        setOlavKeys(
          rawData.filter(
            item => item[currentField] === suggestionsList[0].value,
          )[0] || null,
        );
        focusNextField(currentField);
      } else if (
        type === 'internet' &&
        currentField === 'gebadr1' &&
        wasClicked
      ) {
        const tkconnPost = rawData?.find(
          adr => adr.gebadr1 === formValues[currentField],
        );

        const rawTkconnid = await getTkconidSuggestions(tkconnPost);
        //when tkconnid exists with null value then gebadr1 is valid "door"
        const houseTkconnid = rawTkconnid.details?.find(
          id => id.tuernummer === null,
        );

        if (houseTkconnid) {
          setStorageTkconnid({
            tkconnid: houseTkconnid?.tkconnid,
          });
        }
      }

      // Set suggestions unless 'tuerNr' for 'internet', as 'tuerNr' for 'internet' comes from 'TKconid'
      if (type !== 'internet') {
        setSuggestions({
          ...defaultSuggestions,
          [currentField]: suggestionsList,
        });
      }
      if (currentField !== 'tuerNr' && type === 'internet') {
        setSuggestions({
          ...defaultSuggestions,
          [currentField]: suggestionsList,
          ...(suggestionTkconnid && { tuerNr: suggestionTkconnid }),
        });
      }
    } catch (error) {
      if (error?.response?.status === 404 && currentField !== 'tuerNr') {
        setSuggestions({
          ...defaultSuggestions,
          [currentField]: [
            {
              label: `"${formValues[currentField]}" konnte nicht gefunden werden`,
              value: '',
              icon: false,
              type: 'error',
            },
          ],
        });
      }
      if (error?.response?.status === 500) {
        setServerError(true);
      }
    } finally {
      menus[currentField].show();
      if (suggestions?.tuerNr) {
        menus.tuerNr.show();
      }
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (errorRef.current && isMobile && serverError) {
      errorRef.current.scrollIntoView({
        block: 'center',
        behavior: 'smooth',
      });
    }
  }, [serverError]);

  useEffect(() => {
    // if there is no olavKeys in the storage but other data in the storage
    // we try to build the olavKeys from the data in the form
    // this creates an olavKeys object and we can make a new availabilities query
    if (!Object.keys(storage?.olavKeys || {}).length && values.gebadr1 !== '') {
      // to prevent the query to execute on intial load
      // queryAddress('tuerNr', values, selectedValues, false);
    }
  }, [selectedValues]);

  const handleSuggestionClick = async (fieldName, value) => {
    form.setValue(fieldName, value);

    const formValues = { ...form.getState(), [fieldName]: value };
    queryAddress(fieldName, formValues, selectedValues, true);

    setSelectedValues(prev => ({ ...prev, [fieldName]: value }));

    if (fieldName === 'gebadr1' && value.split('/').length > 1) {
      setSelectedValues(prev => ({
        ...prev,
        stiege: value.split('/')[1],
      }));
    }
    if (fieldName === 'plzOrt' && value.split(' ').length > 1) {
      setSelectedValues(prev => ({
        ...prev,
        gemeinde: value.split(' ')[1],
      }));
    }

    if (
      (type === 'heat' || type === 'general') &&
      fieldName === 'gebadr1' &&
      suggestions.length > 1
    ) {
      setIsButtonLoading(true);
      // Request heat information from WebGIS based on the provided address information
      const relevantAddressInfo = { ...storage, [fieldName]: value };
      await getHeatInformation(
        relevantAddressInfo,
        relevantAddressInfo?.gebaeudeKey,
      );
    }

    focusNextField(fieldName);
  };

  // reset the selected value
  const handleResetValue = field => () => {
    setSelectedValues(prev => ({ ...prev, [field]: '' }));
    form.setValue(field, '');
    Object.keys(fieldDependencies).forEach(key => {
      if (fieldDependencies[key].includes(field)) {
        setSelectedValues(prev => ({ ...prev, [key]: '' }));
        form.setValue(key, '');
      }
    });
    if (field === 'plzOrt') {
      setStorage(defaultValuesAddressPicker);
    }
    if (field === 'gebadr1' && type === 'internet') {
      setStorageTkconnid({ tkconnid: {} });
    }
  };
  // update form and selectedValues on mount
  useEffect(() => {
    if (storage) {
      const {
        Fernwaerme,
        Gas,
        Internet,
        Iptv,
        Strom,
        Voip,
        currentAddress,
        olavKeys,
        tkconnid,
        ...otherValues
      } = storage; // Extract available values, excluding olavKeys

      Object.keys(otherValues).forEach(key => {
        form.setValue(key, storage[key]);
      });

      setSelectedValues(otherValues);
    }
  }, []);

  const debouncedQueryAddress = useCallback(
    debounce(
      (currentField, newValues, fv, storage) =>
        queryAddress(currentField, newValues, fv, false),
      500,
    ),
    [],
  );

  // make a new search query if there are new values typed into the form
  useEffect(() => {
    if (!document.hasFocus() || !document.activeElement) {
      return;
    }

    const fieldName = document.activeElement.getAttribute('name');

    if (!fieldName) {
      return;
    }

    debouncedQueryAddress(fieldName, values, selectedValues, false);
  }, [
    values.plzOrt,
    values.gemeinde,
    values.strasse,
    values.gebadr1,
    values.stiege,
    values.tuerNr,
  ]);

  // store the selectedValues in the storage
  useEffect(() => {
    setStorage({
      ...storage,
      ...selectedValues,
      olavKeys,
      currentAddress: olavKeys?.olavKey
        ? olavKeys.olavKey
        : storage?.currentAddress,
    });
  }, [selectedValues]);

  const isStiegeSkipped =
    selectedValues?.stiege === '' && selectedValues?.tuerNr !== '';

  const preventKeyTab = e => {
    if (e.keyCode === 9) {
      e.preventDefault();
    }
  };

  const handleNewCheck = () => {
    setStorage(defaultValuesAddressPicker);
    form.reset();

    const propertiesToReset = [
      'plzOrt',
      'gemeinde',
      'strasse',
      'gebadr1',
      'stiege',
      'tuerNr',
    ];
    propertiesToReset.forEach(
      property =>
        (selectedValues[property] = defaultValuesAddressPicker[property]),
    );

    setServerError(false);

    // Scroll picker into view if on mobile
    if (isMobile && pickerRef.current) {
      pickerRef.current.scrollIntoView({
        block: 'center',
        behavior: 'smooth',
      });
    }
  };

  const hasEmptyDependencies = name =>
    fieldDependencies[name].some(item => !selectedValues[item]);

  const focusNextField = name => {
    const nextInputRef = focusDependencies[name];

    if (nextInputRef?.current) {
      nextInputRef.current.focus();
    }
  };

  if (isLoading === 'skeleton') {
    return (
      <div className={styles.skeleton}>
        <Skeleton count={4} height="5.78rem" />
      </div>
    );
  }

  return (
    <>
      {subtitle ? (
        <Content className={styles.subtitle} size="lg" withParser>
          {subtitle}
        </Content>
      ) : (
        <Content size="lg">Bitte geben Sie hier Ihre Adresse ein:</Content>
      )}
      <Form onKeyDown={e => preventKeyTab(e)} ref={pickerRef} store={form}>
        <div className={classNames(styles.formGroup)}>
          <div className={classNames(styles.cols2)}>
            <AutocompleteInputField
              disabled={hasEmptyDependencies('plzOrt') || serverError}
              handleResetValue={handleResetValue('plzOrt')}
              handleSuggestionClick={handleSuggestionClick}
              label="PLZ:"
              menu={menus.plzOrt}
              name="plzOrt"
              store={form}
              suggestions={suggestions.plzOrt}
              value={selectedValues.plzOrt}
            />
            <AutocompleteInputField
              disabled={hasEmptyDependencies('gemeinde') || serverError}
              handleResetValue={handleResetValue('gemeinde')}
              handleSuggestionClick={handleSuggestionClick}
              inputRef={gemeindeRef}
              label="Ort:"
              menu={menus.gemeinde}
              name="gemeinde"
              store={form}
              suggestions={suggestions.gemeinde}
              value={selectedValues.gemeinde}
            />
          </div>
          <div className={classNames(styles.cols1)}>
            <AutocompleteInputField
              disabled={hasEmptyDependencies('strasse') || serverError}
              handleResetValue={handleResetValue('strasse')}
              handleSuggestionClick={handleSuggestionClick}
              inputRef={strasseRef}
              label="Straße:"
              menu={menus.strasse}
              name="strasse"
              store={form}
              suggestions={suggestions.strasse}
              value={selectedValues.strasse}
            />
          </div>
          <div className={classNames(styles.cols3)}>
            <AutocompleteInputField
              disabled={hasEmptyDependencies('gebadr1') || serverError}
              handleResetValue={handleResetValue('gebadr1')}
              handleSuggestionClick={handleSuggestionClick}
              inputRef={gebadr1Ref}
              label="Hausnummer:"
              menu={menus.gebadr1}
              name="gebadr1"
              store={form}
              suggestions={suggestions.gebadr1}
              value={selectedValues.gebadr1}
            />
            <AutocompleteInputField
              disabled={hasEmptyDependencies('stiege') || serverError}
              handleResetValue={handleResetValue('stiege')}
              handleSuggestionClick={handleSuggestionClick}
              inputRef={stiegeRef}
              isSkipped={isStiegeSkipped}
              label="Stiege:"
              menu={menus.stiege}
              name="stiege"
              store={form}
              suggestions={suggestions.stiege}
              value={selectedValues.stiege}
            />
            <AutocompleteInputField
              disabled={hasEmptyDependencies('tuerNr') || serverError}
              handleResetValue={handleResetValue('tuerNr')}
              handleSuggestionClick={handleSuggestionClick}
              inputRef={tuerNrRef}
              label="Türnummer:"
              menu={menus.tuerNr}
              name="tuerNr"
              store={form}
              suggestions={suggestions.tuerNr?.filter(name =>
                name?.label?.startsWith(values?.tuerNr),
              )}
              value={selectedValues.tuerNr}
            />
          </div>
        </div>

        <div className={classNames(styles.buttonWrapper)}>
          <FormSubmit
            id={buttonId}
            ref={submitRef}
            render={
              <Button
                className={styles.submitButton}
                disabled={disabledSubmit}
                isElevated
                isLoading={isButtonLoading ? true : false}
                theme={buttonTheme}
              >
                {buttonText}
              </Button>
            }
            store={form}
          />
        </div>
      </Form>
      {serverError && (
        <div className={styles.serverError} ref={errorRef}>
          <Error onNewCheck={handleNewCheck} />
        </div>
      )}
    </>
  );
};

AddressPicker.propTypes = {
  buttonId: PropTypes.string.isRequired,
  buttonText: PropTypes.string,
  buttonTheme: PropTypes.oneOf([
    'primary',
    'secondary',
    'tertiary',
    'ghost',
    'contrast',
  ]),
  onError: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  subtitle: PropTypes.string,
  type: PropTypes.oneOf(['internet', 'heatPump', 'heat', 'general']).isRequired,
};

export default AddressPicker;
